import { useContext, useEffect, useRef, useState } from 'react';
import FormBuilder from '../../../../library/Builders/FormBuilder';
import { SmartContext } from '../../../../library/Core/SmartContext';
import { FormSection } from '../../../../library/Core/SmartTypes';
import FiltersApplied from '../../../../library/SimpleControls/FiltersApplied';
import InstituteAdminGrievanceService, { defaultSearchCriteria } from '../services/InstituteAdminGrievanceService';

const INFO_BANNER_COPY =
    'These grievances have been reviewed and escalated to you by the Univariety alumni relations team. Each item includes our team\u2019s context, recommended priority, and a suggested resolution plan. Grievances are sorted by priority \u2014 please address Critical and High items first.';

const SearchCriteria = () => {
    const { state, dispatch } = useContext(SmartContext);
    const searchFormConfig = state.formConfig?.sectionRepository[0] as FormSection;

    const [fuzzySearchText, setFuzzySearchText] = useState('');
    const [isExpanded, setIsExpanded]           = useState(true);
    // Track the last searchCriteria reference we fetched for, so status/push
    // updates that change other state.data keys don't trigger a spurious re-fetch.
    const lastFetchedCriteriaRef = useRef(state.data.searchCriteria);

    // Re-fetch only when searchCriteria VALUE changes (not on every parent re-render)
    useEffect(() => {
        if (lastFetchedCriteriaRef.current === state.data.searchCriteria) return;
        lastFetchedCriteriaRef.current = state.data.searchCriteria;
        InstituteAdminGrievanceService.search(state.data.searchCriteria).then((response) => {
            dispatch({
                type: 'CONTROL_VALUE_CHANGE',
                payload: { dataKey: 'grievanceList', value: response.grievances ?? [], errorMessages: [] },
            });
        });
    }, [state.data.searchCriteria]);

    const handleFuzzySearch = (event: React.FormEvent) => {
        event.preventDefault();
        const merged = { ...state.data.searchCriteria, search: fuzzySearchText };
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'searchCriteria', value: merged, errorMessages: [] } });
    };

    const handleClearAll = () => {
        const reset = defaultSearchCriteria();
        if (state.data.isSingleCampus && state.data.primaryCampusId) {
            reset.campusId = state.data.primaryCampusId;
        }
        setFuzzySearchText('');
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'searchCriteria', value: reset, errorMessages: [] } });
    };

    return (
        <>
            {/* ── Info banner ─────────────────────────────────────────── */}
            <div className="bg-blue-eff8ff p-4 border-radius-6 mb-4 d-flex gap-3 border border-info-subtle">
                <i className="bi bi-info-square-fill font-22 font-500 text-dark-blue"></i>
                <div className="flex-1">{INFO_BANNER_COPY}</div>
            </div>

            {/* ── Filter row: toggle button + search ──────────────────── */}
            <div className="row justify-content-between align-items-end align-items-sm-center flex-column-reverse flex-sm-row">
                <div className="col">
                    <div className="d-flex flex-wrap justify-content-between align-items-start align-items-md-center mb-2 filterSearch">
                        <div className="d-flex flex-wrap flex-1 mb-0 justify-content-start">
                            <button
                                className="btn-grpfiter me-3"
                                type="button"
                                aria-expanded={isExpanded}
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                <i className="icon-filter font-13 me-2"></i>
                                Filters
                            </button>

                            <div className="search alumniSearch m-0 open position-static flex-1">
                                <form className="form-inline d-flex flex-wrap justify-content-between d-block" onSubmit={handleFuzzySearch}>
                                    <input
                                        type="search"
                                        value={fuzzySearchText}
                                        placeholder={searchFormConfig?.fuzzySearchPlaceholder ?? 'Search grievances...'}
                                        aria-label="Search"
                                        className="form-control flex-1 font-13 font-500 text-truncate"
                                        onChange={(e) => setFuzzySearchText(e.target.value)}
                                    />
                                    <button className="btn btn-search icon icon-search" type="submit"></button>
                                </form>
                                <a href="javascript:;" className="btn-search-toggle icon-search"></a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Collapsible filter panel ─────────────────────────────── */}
            <div className="fitergroup mb-4">
                {isExpanded && (
                    <div className="accordion-collapse collapse show">
                        <div className="accordion-body p-4">
                            <FormBuilder key="searchCriteria" section={searchFormConfig} dataKey="searchCriteria" />

                        </div>
                    </div>
                )}
            </div>

            {/* Applied filter pills */}
            <div className="filterresult">
                <FiltersApplied dataKey="searchCriteria" onClearAll={handleClearAll} />
            </div>
        </>
    );
};

export default SearchCriteria;
